/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Quadrant, ToothSide } from '@libs/ui-lib';
import { ProbingSite } from 'libs/perio/src/lib/common/models';
import { fadeInOnly } from 'libs/perio/src/lib/common/animations';

@Component({
  selector: 'lib-pocket-depth-symbol',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pocket-depth-symbol.component.html',
  styleUrl: './pocket-depth-symbol.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOnly],
})
export class PocketDepthSymbolComponent {
  public probingSite = input<ProbingSite>();
  public isQuadrantSelected = input<boolean>(false);
  public overlayPosition = input<Quadrant | null>(null);
  public toothSide = input<ToothSide | null>(null);
  public exam = input<'current' | 'previous' | undefined>();

  protected bleedingAndSuppurationSymbolSrc = computed<string>(() => {
    if (this.probingSite()?.bleeding && this.probingSite()?.suppuration) {
      return 'assets/images/perio/bleeding-suppuration.svg';
    }
    if (this.probingSite()?.bleeding) {
      return 'assets/images/perio/bleeding.svg';
    }
    if (this.probingSite()?.suppuration) {
      return 'assets/images/perio/suppuration.svg';
    }
    return '';
  });

  protected bleedingAndSuppurationSymbolSizeClass = computed<string>(() => {
    return this.isQuadrantSelected() ? 'h-[28px] w-[28px]' : 'h-[14px] w-[14px]';
  });

  protected bleedingAndSuppurationSymbolAlignClass = computed<string>(() => {
    if (this.exam() === 'current') {
      return this.isQuadrantSelected() ? '-top-12' : '-top-6';
    }
    if (this.exam() === 'previous') {
      return this.isQuadrantSelected() ? '-bottom-12' : '-bottom-6';
    }
    return '';
  });

  protected pocketDepthSymbolSrc = computed<string>(() => {
    switch (this.probingSite()?.pocketDepth) {
      case -1:
      case 0: {
        return '';
      }
      case 1: {
        return 'assets/images/perio/low-rectangle.svg';
      }
      case 2: {
        return 'assets/images/perio/low-triangle.svg';
      }
      case 3: {
        return 'assets/images/perio/high-triangle.svg';
      }
      default: {
        return 'assets/images/perio/high-rectangle.svg';
      }
    }
  });

  protected pocketDepthSymbolSizeClass = computed<string>(() => {
    return this.isQuadrantSelected() ? 'h-[32px] max-w-[28px]' : 'h-[16px] max-w-[14px]';
  });

  protected pocketDepthSymbolAlignClass = computed<string>(() => {
    if ((this.probingSite()?.pocketDepth ?? 0) > 3) return 'items-center';

    const position = this.overlayPosition();
    const side = this.toothSide();

    if (position?.includes('top')) {
      return side === 'buccal' ? 'items-end' : 'items-start';
    }

    if (position?.includes('bottom')) {
      return side === 'lingual' ? 'items-end' : 'items-start';
    }

    return '';
  });

  protected pocketDepthTextClass = computed<string>(() => {
    switch (this.probingSite()?.pocketDepth) {
      case 1: {
        return this.isQuadrantSelected() ? 'typo-h8-semibold' : 'typo-h9-light font-semibold';
      }
      case 2: {
        return this.isQuadrantSelected() ? 'typo-h5-semibold' : 'text-[7px]/[7px]  font-semibold';
      }
      case 3: {
        return this.isQuadrantSelected() ? 'typo-h3-semibold' : 'typo-h8-semibold';
      }
      default: {
        return this.isQuadrantSelected() ? 'typo-h3-semibold' : 'typo-h8-semibold';
      }
    }
  });

  protected pocketDepthSymbolRotation = computed<string>(() => {
    const position = this.overlayPosition();
    const side = this.toothSide();

    if ((position?.includes('top') && side === 'lingual') || (position?.includes('bottom') && side === 'buccal')) {
      return 'rotate-180';
    }

    return '';
  });
}
